/**
 * Page-view tracking helpers for the UBT CJA integration.
 *
 * Two dispatch mechanisms are provided:
 *  - _satellite.kpCustomEvent  — KP-specific custom page-view (primary path)
 *  - _satellite.track          — standard Adobe Launch rule trigger (fallback)
 *
 * Both are queued and flushed automatically if _satellite is not yet loaded
 * (async DTM script has not finished fetching).
 */

import { getFullPageName } from './config';
import { setPageInfo, setContentInfo, logDigitalData } from './dataLayer';

// ── kpCustomEvent queue ───────────────────────────────────────────────────────

/** Pending page-view names waiting for _satellite.kpCustomEvent to be ready. */
const customPageViewQueue: string[] = [];

/**
 * Enqueues a page name and polls every 500 ms until kpCustomEvent is available
 * or 30 seconds have elapsed.
 *
 * @param pageName - Fully-qualified KP page name
 */
const queueCustomPageView = (pageName: string): void => {
  if (globalThis.window === undefined) return;
  customPageViewQueue.push(pageName);

  const startTime = Date.now();
  const interval = setInterval(() => {
    // Guard against window being undefined (test teardown scenario)
    if (!globalThis.window) {
      clearInterval(interval);
      return;
    }

    if (globalThis.window._satellite?.kpCustomEvent) {
      console.log(`kpCustomEvent ready — flushing ${customPageViewQueue.length} queued views`);
      while (customPageViewQueue.length > 0) {
        const name = customPageViewQueue.shift();
        // Double-check window still exists before calling
        if (name && globalThis.window?._satellite?.kpCustomEvent) {
          globalThis.window._satellite.kpCustomEvent('cjaCustomPageview', { pageName: name });
        }
      }
      clearInterval(interval);
    } else if (Date.now() - startTime > 30_000) {
      console.warn('kpCustomEvent not available after 30 s — clearing queue');
      customPageViewQueue.length = 0;
      clearInterval(interval);
    }
  }, 500);
};

// ── Core page-view tracker ────────────────────────────────────────────────────

/**
 * Tracks a page view via _satellite.kpCustomEvent("cjaCustomPageview", { pageName }).
 *
 * Steps:
 * 1. Resolves the full KP page name from the relative page key.
 * 2. Writes setPageInfo + setContentInfo into window.digitalData.
 * 3. Fires kpCustomEvent (or queues it if _satellite is not ready yet).
 *
 * @param pageKey    - A value from ANALYTICS_CONFIG.PAGES (relative to BASE_PAGE_NAME)
 * @param pageData   - Optional additional page metadata
 */
export const trackPageView = (
  pageKey: string,
  pageData?: {
    pageTemplate?: string;
    pageType?: string;
    contentTitle?: string;
    contentType?: string;
  }
): void => {
  try {
    const pageName = getFullPageName(pageKey);

    setPageInfo(pageName, pageData);
    setContentInfo({
      title: pageData?.contentTitle ?? pageKey,
      type: pageData?.contentType ?? 'page',
    });

    // Use optional chaining for safe access
    const kpCustomEvent = globalThis.window?._satellite?.kpCustomEvent;
    if (kpCustomEvent) {
      kpCustomEvent('cjaCustomPageview', { pageName });
    } else {
      queueCustomPageView(pageName);
    }

    logDigitalData();
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};
